/** Global errors do not necessarily reach a React error boundary. */
export function installRuntimeErrorMonitor(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'>,
  report: (error: Error, source: string) => Promise<unknown>,
) {
  const seen = new WeakSet<Error>();
  const normalizedReasons = new WeakMap<object, Error>();
  const capture = (reason: unknown, source: string) => {
    let error = reason instanceof Error ? reason : new Error(
      typeof reason === 'string' ? reason : 'Unhandled non-Error rejection',
    );
    if (reason && typeof reason === 'object' && !(reason instanceof Error)) {
      const existing = normalizedReasons.get(reason);
      if (existing) error = existing;
      else {
        // Cross-realm Errors and error-shaped rejections fail instanceof Error.
        // Keep their exception fields without serializing arbitrary user data.
        try {
          const detail = reason as Record<string, unknown>;
          if (typeof detail.message === 'string' && detail.message) error = new Error(detail.message.slice(0, 8000));
          if (typeof detail.name === 'string' && detail.name) error.name = detail.name.slice(0, 160);
          if (typeof detail.stack === 'string') error.stack = detail.stack.slice(0, 8000);
          for (const key of ['type', 'request', 'dobiChunkRecovery', 'dobiManifestRecovery'] as const) {
            if (detail[key] !== undefined) Object.assign(error, { [key]: detail[key] });
          }
        } catch { /* Throwing accessors must not hide the original rejection. */ }
        normalizedReasons.set(reason, error);
      }
    }
    if (error.name === 'AbortError' || seen.has(error)) return;
    seen.add(error);
    // Reporting must not recursively create another unhandled rejection.
    void report(error, source).catch(() => undefined);
  };
  const onError = (event: ErrorEvent) => {
    // Resource load events have no JS exception. Do not mistake a missing
    // thumbnail or a cross-origin opaque "Script error" for an application bug.
    if (event.error) capture(event.error, 'window.error');
    else if (event.message && event.message !== 'Script error.') capture(event.message, 'window.error');
  };
  const onRejection = (event: PromiseRejectionEvent) => capture(event.reason, 'unhandledrejection');
  target.addEventListener('error', onError as EventListener);
  target.addEventListener('unhandledrejection', onRejection as EventListener);
  return () => {
    target.removeEventListener('error', onError as EventListener);
    target.removeEventListener('unhandledrejection', onRejection as EventListener);
  };
}
